/* eslint react/destructuring-assignment: 0 */
/* eslint @typescript-eslint/camelcase: 0 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { HotKeys } from 'react-hotkeys';
import { isDirty } from 'redux-form';
import Preloader from '@omilia/preloader';

import uuid from 'uuid/v4';
import _has from 'lodash/has';
import _omit from 'lodash/omit';
import _times from 'lodash/times';
import _findIndex from 'lodash/findIndex';

import Notify from 'store/utils/Notify';

import COMBOS from 'constants/keyboardShortcutsCombos';
import FORM_NAMES from 'constants/formNames';
import * as TYPES from 'types';

import withPartialImportModal from 'components/withPartialImportModal';
import { withExportingModal } from 'components/PartialExportModal';
import Splitter from 'components/Splitter';
import UnitsSearchResult from 'components/Units/UnitsSearchResult';
import NoUnits from 'components/Units/NoUnits';
import NoUnitsConcepts from 'components/Units/NoUnitsConcepts';
import Pagination from 'components/Pagination';
import ItemHeaderOptions from 'components/ItemHeaderOptions';

import * as ruleActions from 'store/services/rules/actions';
import * as ruleSelectors from 'store/services/rules/selectors';
import * as ruleGroupsActions from 'store/services/ruleGroups/actions';
import * as ruleGroupsSelectors from 'store/services/ruleGroups/selectors';
import * as testSetsSelectors from 'store/services/testSets/selectors';
import * as testSetEntriesSelectors from 'store/services/testSetEntries/selectors';
import * as annotationsActions from 'store/services/annotations/actions';
import * as annotationsSelectors from 'store/services/annotations/selectors';

import TestSets from 'scenes/TestSets';

import List from './components/List';
import InspectRule from './components/InspectRule';
import InspectGroup from './components/InspectGroup';

import CreateRuleModal from './components/CreateNewRuleModal';
import CreateGroupModal from './components/CreateRuleGroupOrRuleModal';

import { createCopyRuleMatchingObj } from './utils';

import './style.scss';

const HOTKEYS_MAP = {
  create: COMBOS.createNew,
};

const DEFAULT_CREATE_GROUP_RULE_MODAL = {
  isOpen: false,
  payload: {
    groupId: null,
    count: null,
    order: null,
  },
};

const DEFAULT_DELETE_GROUP_MODAL = {
  isOpen: false,
  payload: {
    id: null,
    name: null,
  },
};

const DEFAULT_DUPLICATE_RULE_MODAL = {
  isOpen: false,
  payload: {
    id: null,
    ruleName: null,
  },
};

/* eslint-disable camelcase */
/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
export class Rules extends Component {
  handleHotKeys = {
    create: this.handleOpenCreateGroupModal,
  };

  static propTypes = {
    // RULE GROUPS:
    annotationsIsLoaded: PropTypes.bool.isRequired,
    annotationsIsLoading: PropTypes.bool.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    inspectingDirty: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,

    match: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }).isRequired,
    partialExport: PropTypes.func.isRequired,

    ruleGroupsIsDeleting: PropTypes.bool.isRequired,
    ruleGroupsIsLoading: PropTypes.bool.isRequired,
    ruleGroupsIsReordering: PropTypes.bool.isRequired,

    ruleGroupsIsUpdating: PropTypes.bool.isRequired,
    testSetsHasSelected: PropTypes.bool.isRequired,
    onAnnotatorExecute: PropTypes.func.isRequired,
    onAnnotatorToggleAllVisibility: PropTypes.func.isRequired,
    onAnnotatorToggleVisibility: PropTypes.func.isRequired,
    onRuleGroupsAddGroup: PropTypes.func.isRequired,
    onRuleGroupsAddGroupWithRule: PropTypes.func.isRequired,
    onRuleGroupsAddRule: PropTypes.func.isRequired,
    onRuleGroupsDelete: PropTypes.func.isRequired,
    onRuleGroupsDeselectGroup: PropTypes.func.isRequired,
    onRuleGroupsDeselectRule: PropTypes.func.isRequired,
    onRuleGroupsGet: PropTypes.func.isRequired,
    onRuleGroupsGroupGet: PropTypes.func.isRequired,

    onRuleGroupsMove: PropTypes.func.isRequired,
    onRuleGroupsPartialUpdateGroup: PropTypes.func.isRequired,

    // PARTIAL IMPORT-EXPORT
    onRuleGroupsPartialUpdateRule: PropTypes.func.isRequired,
    onRuleGroupsReorder: PropTypes.func.isRequired,
    /* eslint-disable react/forbid-prop-types */
    onRuleGroupsRuleGet: PropTypes.func.isRequired,

    // FULL RULE GROUPS:
    onRuleGroupsUpdateGroup: PropTypes.func.isRequired,

    // INSPECTING:
    onRuleGroupsUpdateRule: PropTypes.func.isRequired,
    annotationsMatched: PropTypes.arrayOf(PropTypes.shape({})),
    annotationsMatchedRulesMap: PropTypes.shape({}),
    fullRuleGroups: PropTypes.arrayOf(TYPES.ruleGroupType),

    // TEST-SETS:
    inspectingGroup: TYPES.ruleGroupType,

    // TEST-SET-ENTRIES:
    inspectingIndex: PropTypes.number,
    inspectingRule: TYPES.ruleGroupType,

    // ANNOTATIONS:
    ruleGroups: PropTypes.arrayOf(TYPES.ruleGroupType),
    ruleGroupsCount: PropTypes.number,
    ruleGroupsFilterOptions: PropTypes.arrayOf(PropTypes.shape({})),

    ruleGroupsFilterOptionsSelected: PropTypes.arrayOf(PropTypes.shape({})),
    ruleGroupsOrderList: PropTypes.arrayOf(PropTypes.shape({})),
    ruleGroupsPagination: TYPES.paginationOptionsType,

    // REACT-ROUTER:
    ruleGroupsTotalCount: PropTypes.number,

    testSetsEntries: PropTypes.arrayOf(PropTypes.shape({})),

    testSetsEntriesPagination: TYPES.paginationOptionsType,
  };

  state = {
    isCreateRuleModalOpen: false,
    isCreateGroupModalOpen: false,
    isUnsavedChangesModalOpen: false,
    modalDeleteGroup: DEFAULT_DELETE_GROUP_MODAL,
    modalDuplicateRule: DEFAULT_DUPLICATE_RULE_MODAL,
    modalCreateGroupRule: DEFAULT_CREATE_GROUP_RULE_MODAL,
    selectedRule: undefined,
  };

  componentDidMount = () => {
    this.props.onRuleGroupsGet({
      page: 1,
      search: '',
      ordering: '-created_at',
    });
  };

  getParentGroup = ({ rule_group }) =>
    this.props.fullRuleGroups.find(({ id }) => id === rule_group);

  getRuleMatchedRows = (rule, matchedRulesMap) => {
    if (!matchedRulesMap || !matchedRulesMap[rule.id]) {
      return;
    }

    const matchedRule = matchedRulesMap[rule.id];
    const { page, size } = this.props.testSetsEntriesPagination;

    matchedRule.test_set_indexes = [];
    matchedRule.test_set_ids.forEach(testSetId => {
      const order = _findIndex(
        this.props.testSetsEntries,
        ({ id }) => id === testSetId,
      );

      if (order >= 0) {
        matchedRule.test_set_indexes.push((page - 1) * size + order);
      }
    });

    if (matchedRule) {
      matchedRule.test_set_indexes.sort((a, b) => a - b);
    }

    rule.matchedRows = matchedRule ? matchedRule.test_set_indexes : null;

    return rule.matchedRows;
  };

  handleSubmitGroupForm = async ({ name, description, enabled, order }) => {
    await this.props.onRuleGroupsUpdateGroup(this.props.inspectingGroup.id, {
      name,
      enabled,
      description,
      order: order - 1,
    });
  };

  handleSubmitGroupWithRule = async ({ ruleName }) => {
    const payload = {
      name: ruleName,
      description: '',
      actions: [],
      conditions: [],
      matching_pattern_elements: [],
    };
    const { id, groupId } = await this.props.onRuleGroupsAddGroupWithRule(
      payload,
      this.props.ruleGroupsTotalCount,
    );

    if (id && groupId) {
      this.props.history.push(`${this.props.match.url}/${groupId}/rules/${id}`);

      this.handleCloseCreateRuleModal();
    }
  };

  handleSubmitRule = async ({ ruleGroupName, order }) => {
    const { groupId } = this.state.modalCreateGroupRule.payload;

    const { id } = await this.props.onRuleGroupsAddRule(groupId, {
      name: ruleGroupName,
      description: '',
      actions: [],
      conditions: [],
      matching_pattern_elements: [],
      order:
        +order % 1 !== 0
          ? parseInt(order.toString().split('.')[1], 10) - 1
          : order - 1,
    });

    if (id && groupId) {
      //  First we have to await for modal to close and then change the url(which opens the rule inspect window)
      //  If we dont follow that order the modal freezes and does not close,
      //  which is probably caused because of the opening of inspect window.

      await this.handleCloseCreateGroupRuleModal();

      this.props.history.push(`${this.props.match.url}/${groupId}/rules/${id}`);
    }
  };

  handleSubmitGroup = async ({ ruleGroupName, order }) => {
    const payload = {
      name: ruleGroupName,
      description: '',
      order: order - 1,
    };

    const { groupId } = await this.props.onRuleGroupsAddGroup(
      payload,
      this.props.ruleGroupsTotalCount,
    );

    if (groupId) {
      this.props.history.push(`${this.props.match.url}/${groupId}`);

      this.handleCloseCreateGroupModal();
    }
  };

  handleSubmitDuplicate = async ({ ruleName }) => {
    const {
      isGrouped,
      groupId,
      order,
      copy,
    } = this.state.modalDuplicateRule.payload;

    const payload = { name: ruleName, ...copy };
    const { id, groupId: newGroupId } = isGrouped
      ? await this.props.onRuleGroupsAddRule(groupId, payload)
      : await this.props.onRuleGroupsAddGroupWithRule(payload, order + 1);

    if (id && newGroupId) {
      this.props.history.push(
        `${this.props.match.url}/${newGroupId}/rules/${id}`,
      );

      this.handleCloseDuplicateRuleModal();
    }
  };

  handleDeleteGroup = async () => {
    await this.props.onRuleGroupsDelete(this.state.modalDeleteGroup.payload.id);

    this.handleCloseDeleteGroupModal();
    this.handleListItemDeselect();
  };

  handleOpenCreateGroupRuleModal = (id, count, order) => {
    this.setState({
      modalCreateGroupRule: {
        isOpen: true,
        payload: {
          count,
          groupId: id,
          order: order + 1,
          orderList: _times(count, val => ({
            order: `${order + 1}.${val + 1}`,
            index: val + 1,
          })),
        },
      },
    });
  };

  handleCloseCreateGroupRuleModal = () =>
    this.setState({
      modalCreateGroupRule: DEFAULT_CREATE_GROUP_RULE_MODAL,
    });

  handleOpenDeleteGroupModal = (id, name) => {
    this.setState({
      modalDeleteGroup: {
        isOpen: true,
        payload: {
          id,
          name,
        },
      },
    });
  };

  handleCloseDeleteGroupModal = () => {
    this.setState({
      modalDeleteGroup: DEFAULT_DELETE_GROUP_MODAL,
    });
  };

  handleOpenCreateRuleModal = () => {
    this.setState({
      isCreateRuleModalOpen: true,
    });
  };

  handleOpenCreateGroupModal = () => {
    this.setState({
      isCreateGroupModalOpen: true,
    });
  };

  handleOpenDuplicateRuleModal = (model, isGrouped) => {
    const newId = uuid();
    const matchingPatternsCopy = createCopyRuleMatchingObj(
      model,
      'matching_pattern_elements',
      newId,
    );

    // TODO: Replace with _pick
    const ruleCopy = _omit(model, [
      'id',
      'archived',
      'contains_archived_resources',
      'created_at',
      'name',
      'project',
      'updated_at',
      'xpack',
      'order',
      'actions',
      'conditions',
      'matching_pattern_elements',
      'rule_group',
    ]);

    const { order } = this.props.fullRuleGroups.find(
      ({ id }) => id === model.rule_group,
    );

    this.setState({
      modalDuplicateRule: {
        isOpen: true,
        payload: {
          order,
          isGrouped,
          groupId: model.rule_group,
          name: model.name,
          copy: {
            id: newId,
            order: isGrouped ? model.order + 1 : 0,
            ...ruleCopy,
            ...matchingPatternsCopy,
          },
        },
      },
    });
  };

  handleCloseCreateRuleModal = () => {
    this.setState({
      isCreateRuleModalOpen: false,
    });
  };

  handleCloseCreateGroupModal = () => {
    this.setState({
      isCreateGroupModalOpen: false,
    });
  };

  handleCloseDuplicateRuleModal = () => {
    this.setState({
      modalDuplicateRule: DEFAULT_DUPLICATE_RULE_MODAL,
    });
  };

  handleSearch = term => {
    this.props.onRuleGroupsGet({
      page: 1,
      search: term,
      ordering: 'name',
    });
  };

  handleFilter = params => {
    this.props.onRuleGroupsGet({ page: 1 }, params);
  };

  handleResetSearch = () => {
    this.props.onRuleGroupsGet({
      page: 1,
      search: '',
    });
  };

  handleInspectGroupSelect = async groupId => {
    await this.props.onRuleGroupsGroupGet(groupId);
    await this.props.onRuleGroupsDeselectRule();
  };

  handleInspectRuleSelect = async (ruleId, groupId) => {
    await this.props.onRuleGroupsRuleGet(ruleId, groupId);
    await this.props.onRuleGroupsDeselectGroup();
  };

  handleOpenUnsavedChangesModal = () => {
    this.setState({ isUnsavedChangesModalOpen: true });
  };

  handleCloseUnsavedChangesModal = () => {
    this.setState({ isUnsavedChangesModalOpen: false });
  };

  handleChangeRule = () => {
    this.handleCloseUnsavedChangesModal();
    this.openRule(this.state.selectedRule);
    this.setState({ selectedRule: undefined });
  };

  openRule = item => {
    this.props.history.push(
      this.props.match.url +
        (item.rule_group
          ? `/${item.rule_group}/rules/${item.id}`
          : `/${item.id}`),
    );
  };

  handleListItemSelect = item => {
    if (this.props.inspectingDirty) {
      this.setState({ selectedRule: item });
      this.handleOpenUnsavedChangesModal();
    } else {
      this.openRule(item);
    }
  };

  handleListItemDeselect = (forceClose = false) => {
    if (!this.props.inspectingDirty || forceClose) {
      this.props.history.push(this.props.match.url);
      this.props.onRuleGroupsDeselectRule();
      this.props.onRuleGroupsDeselectGroup();
    }
  };

  handleListItemToggleStatus = async (id, name, data, mode, groupId) => {
    if (mode === 'Rule') {
      await this.props.onRuleGroupsPartialUpdateRule(id, data, groupId);
    } else {
      await this.props.onRuleGroupsPartialUpdateGroup(id, data);
    }

    if (data.enabled) {
      Notify.success({
        title: `${mode} was enabled successfully!`,
      });
    } else {
      Notify.warning({
        title: 'Warning!',
        description: `${mode} ${name} was disabled!`,
      });
    }
  };

  handleMergeImport = () => {
    this.props.history.push(`${this.props.match.url}/import/merge`);
  };

  handleReplaceImport = () => {
    this.props.history.push(`${this.props.match.url}/import/replace`);
  };

  // TODO: Should go to action
  handleMoveRuleToGroup = async (rule, newRule, expand) => {
    // drop dragged item from list
    await this.props.onRuleGroupsMove(rule, newRule.id);

    let ret;

    // when drag and drop items are Rules
    if (_has(rule, 'rule_group') && _has(newRule, 'rule_group')) {
      const group = this.getParentGroup(newRule);
      const newGroup = this.getParentGroup(rule);
      const ruleGroup = group.visible ? newRule.rule_group : '';
      const order = ruleGroup !== '' ? newRule.order : group.order;

      // when Rules parent groups are visible
      if (newGroup.visible || group.visible) {
        ret = await this.props.onRuleGroupsPartialUpdateRule(
          rule.id,
          {
            order,
            rule_group: ruleGroup,
          },
          rule.rule_group,
          'move',
        );
      } else {
        ret = await this.props.onRuleGroupsPartialUpdateGroup(
          newGroup.id,
          {
            order: newRule.order,
          },
          rule.rule_group,
          'move',
        );
      }
    } else {
      const group = _has(rule, 'rule_group') ? this.getParentGroup(rule) : rule;

      // drop item is Group and drag item is rule
      if (_has(rule, 'rule_group') && _has(newRule, 'rules')) {
        const len = newRule.rules.length;

        // when Group is visible, empty, not archived or collapsed
        if (
          (newRule.visible &&
            len === 0 &&
            !newRule.archived &&
            !newRule.last) ||
          expand
        ) {
          const order = expand ? len : 0;
          ret = await this.props.onRuleGroupsPartialUpdateRule(
            rule.id,
            {
              rule_group: newRule.id,
              order,
            },
            rule.rule_group,
            'move',
          );
        } else if (
          newRule.visible &&
          (rule.rule_group === newRule.id || this.getParentGroup(rule).visible)
        ) {
          ret = await this.props.onRuleGroupsPartialUpdateRule(
            rule.id,
            {
              rule_group: '',
              order: newRule.order,
            },
            rule.rule_group,
            'move',
          );
        } else {
          ret = await this.props.onRuleGroupsPartialUpdateGroup(
            group.id,
            { order: newRule.order },
            rule.rule_group,
            'move',
          );
        }
      } else {
        ret = await this.props.onRuleGroupsPartialUpdateGroup(
          group.id,
          { order: newRule.order },
          rule.rule_group,
          'move',
        );
      }
    }

    if (
      ret &&
      this.props.inspectingRule &&
      this.props.inspectingRule.id === ret.id
    ) {
      this.props.history.push(
        `${this.props.match.url}/${ret.groupId}/rules/${ret.id}`,
      );
    }
  };

  renderModals = () => {
    const {
      isCreateRuleModalOpen,
      isCreateGroupModalOpen,
      modalDuplicateRule,
    } = this.state;

    const { ruleGroupsTotalCount, ruleGroupsOrderList } = this.props;

    return (
      <React.Fragment>
        {isCreateRuleModalOpen && (
          <CreateRuleModal
            onClose={this.handleCloseCreateRuleModal}
            onSubmit={this.handleSubmitGroupWithRule}
          />
        )}

        {isCreateGroupModalOpen && (
          <CreateGroupModal
            count={ruleGroupsTotalCount}
            modelName="group"
            orderList={ruleGroupsOrderList}
            onClose={this.handleCloseCreateGroupModal}
            onSubmit={this.handleSubmitGroup}
          />
        )}

        {modalDuplicateRule.isOpen && (
          <CreateRuleModal
            initialValue={`${modalDuplicateRule.payload.name}_copy`}
            onClose={this.handleCloseDuplicateRuleModal}
            onSubmit={this.handleSubmitDuplicate}
          />
        )}
      </React.Fragment>
    );
  };

  renderList = () => {
    const { modalDeleteGroup, modalCreateGroupRule } = this.state;

    const {
      ruleGroups,
      ruleGroupsTotalCount,
      ruleGroupsPagination,
      ruleGroupsOrderList,
      ruleGroupsIsLoading,
      ruleGroupsIsDeleting,
      ruleGroupsIsUpdating,
      ruleGroupsIsReordering,
      onRuleGroupsUpdateRule,
      onRuleGroupsReorder,
      onRuleGroupsPartialUpdateRule,
      onRuleGroupsPartialUpdateGroup,
      fullRuleGroups,
      inspectingRule,
      inspectingIndex,
      inspectingGroup,
      annotationsMatchedRulesMap,
      testSetsHasSelected,
    } = this.props;

    if (ruleGroups.length > 0) {
      return (
        <>
          {ruleGroupsIsLoading && <Preloader showBackdrop />}
          <List
            count={ruleGroupsTotalCount}
            currentRuleIndex={inspectingIndex}
            deleteRuleGroup={this.handleDeleteGroup}
            deselectRule={this.handleListItemDeselect}
            fullGroups={fullRuleGroups}
            getRuleMatchedRows={this.getRuleMatchedRows}
            groupCount={modalCreateGroupRule.payload.count}
            groupName={modalDeleteGroup.payload.name}
            groupOrder={modalCreateGroupRule.payload.order}
            isAddNewRuleModalOpen={modalCreateGroupRule.isOpen}
            isDeleteGroupModalOpen={modalDeleteGroup.isOpen}
            isDeleting={ruleGroupsIsDeleting}
            isReordering={ruleGroupsIsReordering}
            isUpdating={ruleGroupsIsUpdating}
            matchedRulesMap={annotationsMatchedRulesMap}
            moveRuleToGroup={this.handleMoveRuleToGroup}
            orderList={
              modalCreateGroupRule.isOpen
                ? modalCreateGroupRule.payload.orderList
                : ruleGroupsOrderList
            }
            partialUpdateGroup={onRuleGroupsPartialUpdateGroup}
            partialUpdateRule={onRuleGroupsPartialUpdateRule}
            reorderGroup={onRuleGroupsReorder}
            reorderRule={onRuleGroupsReorder}
            ruleGroupItem={inspectingRule}
            rules={ruleGroups}
            searchTerm={ruleGroupsPagination.search}
            selectedRule={inspectingRule}
            selectedRuleGroup={inspectingGroup}
            selectRule={this.handleListItemSelect}
            selectRuleGroupItem={this.handleListItemSelect}
            toggleRuleStatus={this.handleListItemToggleStatus}
            updateRule={onRuleGroupsUpdateRule}
            onCloseAddRuleModal={this.handleCloseCreateGroupRuleModal}
            // TODO: combine those two methods into one
            onCloseDeleteModal={this.handleCloseDeleteGroupModal}
            onOpenAddRuleModal={this.handleOpenCreateGroupRuleModal}
            onOpenDeleteModal={this.handleOpenDeleteGroupModal}
            onOpenDuplicateRuleModal={this.handleOpenDuplicateRuleModal}
            onSaveNewRule={this.handleSubmitRule}
          />
        </>
      );
    }

    if (ruleGroupsPagination.search !== '') {
      return (
        <UnitsSearchResult
          items={ruleGroups}
          searchTerm={ruleGroupsPagination.search}
          showAll={this.handleResetSearch}
          unitName="rule group & rule"
        />
      );
    }

    if (testSetsHasSelected && !ruleGroups.length) {
      return (
        <NoUnitsConcepts
          resetMatchedFilter={this.handleResetSearch}
          unitName="context rules"
        />
      );
    }

    return (
      <NoUnits openModal={this.handleOpenCreateRuleModal} unitName="rules" />
    );
  };

  renderInspect = () => {
    const {
      location,
      ruleGroups,
      ruleGroupsOrderList,
      onAnnotatorExecute,
      inspectingGroup,
      onRuleGroupsDeselectRule,
      onRuleGroupsDeselectGroup,
    } = this.props;

    if (ruleGroups.length > 0) {
      const matchRule = matchPath(location.pathname, {
        exact: true,
        path: '/:company/:project/groups/:group/rules/:rule',
      });

      if (matchRule) {
        return (
          <InspectRule
            executeAnnotator={onAnnotatorExecute}
            groupId={matchRule.params.group}
            ruleId={matchRule.params.rule}
            selectedRule={!!this.state.selectedRule}
            showUnsavedChangesModal={this.state.isUnsavedChangesModalOpen}
            onChangeRule={this.handleChangeRule}
            onClose={this.handleListItemDeselect}
            onCloseUnsavedChangesModal={this.handleCloseUnsavedChangesModal}
            onDeselect={onRuleGroupsDeselectRule}
            onSelect={this.handleInspectRuleSelect}
          />
        );
      }

      const matchGroup = matchPath(location.pathname, {
        exact: true,
        path: '/:company/:project/groups/:group',
      });

      if (matchGroup) {
        return (
          <InspectGroup
            currentGroup={inspectingGroup}
            groupId={matchGroup.params.group}
            orderList={ruleGroupsOrderList}
            selectedRule={!!this.state.selectedRule}
            showUnsavedChangesModal={this.state.isUnsavedChangesModalOpen}
            onChangeRule={this.handleChangeRule}
            onChangeTab={this.handleChangeTab}
            onClose={this.handleListItemDeselect}
            onCloseUnsavedChangesModal={this.handleCloseUnsavedChangesModal}
            onDeselect={onRuleGroupsDeselectGroup}
            onOpenAddRuleModal={this.handleOpenCreateGroupRuleModal}
            onSelect={this.handleInspectGroupSelect}
            onSubmit={this.handleSubmitGroupForm}
          />
        );
      }
    }

    return null;
  };

  render() {
    const {
      partialExport,
      ruleGroups,
      ruleGroupsCount,
      ruleGroupsPagination,
      ruleGroupsFilterOptions,
      ruleGroupsFilterOptionsSelected,
      onRuleGroupsGet,
      testSetsHasSelected,
      annotationsIsLoaded,
      annotationsIsLoading,
      annotationsMatched,
      onAnnotatorToggleVisibility,
      onAnnotatorToggleAllVisibility,
    } = this.props;

    return (
      <Splitter split="vertical">
        <TestSets />

        <HotKeys
          handlers={this.handleHotKeys}
          keyMap={HOTKEYS_MAP}
          styleName="root"
        >
          {this.renderModals()}

          <ItemHeaderOptions
            hasFilterMatchedMenu
            hasGroups
            hasLeftSubHeader
            annotationsGotLoaded={annotationsIsLoaded}
            annotationsIsLoading={annotationsIsLoading}
            filterItems={this.handleFilter}
            filterOptions={ruleGroupsFilterOptions}
            getItems={onRuleGroupsGet}
            hasSelectedTestSet={testSetsHasSelected}
            items={ruleGroups}
            matchedAnnotations={annotationsMatched}
            modelName="rules"
            openCreateGroupModal={this.handleOpenCreateGroupModal}
            openNewModal={this.handleOpenCreateRuleModal}
            searchTerm={ruleGroupsPagination.search}
            selectedFilteredOption={ruleGroupsFilterOptionsSelected}
            subHeaderLayout="justify"
            toggleAll={onAnnotatorToggleAllVisibility}
            toggleAnnotation={onAnnotatorToggleVisibility}
            onExport={partialExport}
            onMergeImport={this.handleMergeImport}
            onReplaceImport={this.handleReplaceImport}
            onSearchItems={this.handleSearch}
          />

          <div className="tabs-content right-tabs height-184">
            <Splitter split="horizontal">
              {this.renderList()}
              {this.renderInspect()}
            </Splitter>
          </div>

          {ruleGroups.length > 0 && (
            <Pagination
              className="footer__pagination"
              count={ruleGroupsCount}
              getItems={onRuleGroupsGet}
              paginationOptions={ruleGroupsPagination}
            />
          )}
        </HotKeys>
      </Splitter>
    );
  }
}

const mapStateToProps = state => ({
  isExporting: ruleSelectors.getIsExporting(state),
  // RULE GROUPS:
  ruleGroups: ruleGroupsSelectors.getRuleGroupsListWithEnabledKey(state),
  ruleGroupsCount: ruleGroupsSelectors.getRuleGroupsListCount(state),
  ruleGroupsTotalCount: ruleGroupsSelectors.getRuleGroupsListTotalCount(state),
  ruleGroupsPagination: ruleGroupsSelectors.getPaginationOptions(state),
  ruleGroupsOrderList: ruleGroupsSelectors.getRuleGroupsOrderList(state),

  ruleGroupsFilterOptions: ruleGroupsSelectors.getFilterOptions(state),
  ruleGroupsFilterOptionsSelected: ruleGroupsSelectors.getSelectedFilteredOption(
    state,
  ),

  ruleGroupsIsLoading: ruleGroupsSelectors.getIsLoadingList(state),
  ruleGroupsIsDeleting: ruleGroupsSelectors.getIsDeleting(state),
  ruleGroupsIsUpdating: ruleSelectors.getIsUpdating(state),
  ruleGroupsIsReordering: ruleSelectors.getIsReordering(state),

  // FULL RULE GROUPS:
  fullRuleGroups: ruleGroupsSelectors.getFullRuleGroupsList(state),

  // INSPECTING:
  inspectingRule: ruleSelectors.getRule(state),
  inspectingGroup: ruleGroupsSelectors.getRuleGroup(state),
  inspectingIndex: ruleGroupsSelectors.getRuleIndex(state),
  inspectingDirty:
    isDirty(FORM_NAMES.RULES.INSPECT)(state) ||
    isDirty(FORM_NAMES.GROUPS.INSPECT)(state),
  // TEST-SETS:
  testSetsHasSelected: testSetsSelectors.getHasSelectedTestSet(state),

  // TEST-SETS-ENTRIES:
  testSetsEntries: testSetEntriesSelectors.getTestSetEntriesList(state),
  testSetsEntriesPagination: testSetEntriesSelectors.getPaginationOptions(
    state,
  ),

  // ANNOTATIONS:
  annotationsIsLoaded: annotationsSelectors.getIsLoaded(state),
  annotationsIsLoading: annotationsSelectors.getIsLoading(state),
  annotationsMatchedRulesMap: annotationsSelectors.getMatchedRulesMap(state),
  annotationsMatched: annotationsSelectors.getAllMatchedAnnotations(state),
});

const mapDispatchToProps = {
  partialExport: ruleActions.partialExportAllRules,
  cancelExport: ruleActions.cancelExportRules,
  onRuleGroupsGet: ruleGroupsActions.getRuleGroupsList,
  onRuleGroupsAddRule: ruleActions.addRule,
  onRuleGroupsAddGroup: ruleGroupsActions.addRuleGroup,
  onRuleGroupsAddGroupWithRule: ruleGroupsActions.addRuleGroupWithRule,
  onRuleGroupsDeselectRule: ruleActions.deselectRule,
  onRuleGroupsDeselectGroup: ruleGroupsActions.deselectRuleGroup,
  onRuleGroupsPartialUpdateRule: ruleActions.partialUpdateRule,
  onRuleGroupsPartialUpdateGroup: ruleGroupsActions.partialUpdateRuleGroup,
  onRuleGroupsDelete: ruleGroupsActions.deleteRuleGroup,
  onRuleGroupsUpdateRule: ruleActions.updateRule,
  onRuleGroupsUpdateGroup: ruleGroupsActions.updateRuleGroup,
  onRuleGroupsReorder: ruleGroupsActions.reorderRuleGroup,
  onRuleGroupsMove: ruleGroupsActions.moveRuleToList,

  onRuleGroupsRuleGet: ruleActions.getRule,
  onRuleGroupsGroupGet: ruleGroupsActions.getRuleGroup,

  // ANNOTATIONS:
  onAnnotatorExecute: annotationsActions.annotate,
  onAnnotatorToggleVisibility: annotationsActions.toggleAnnotationVisibility,
  onAnnotatorToggleAllVisibility:
    annotationsActions.toggleAnnotationsVisibility,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withPartialImportModal('rules'),
  withExportingModal('Rules'),
)(Rules);
